import React from 'react';

import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogVideo?: string;
  ogArticlePublishedTime?: string;
  ogArticleModifiedTime?: string;
  ogArticleAuthor?: string;
  ogArticleTags?: string[];
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  twitterSite?: string;
  twitterCreator?: string;
  structuredData?: Record<string, any>;
  additionalStructuredData?: Record<string, any>[];
  noIndex?: boolean;
  siteName?: string;
  locale?: string;
  alternateLanguages?: { locale: string; url: string }[];
  pageType?: 'home' | 'service' | 'blog' | 'contact' | 'about' | 'plan' | 'case-study';
  lastModified?: string;
  author?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  canonicalUrl = '',
  ogImage = '/og-image.jpg',
  ogImageAlt = 'SOL IN PRIME JC',
  ogType = 'website',
  ogImageWidth = 1200,
  ogImageHeight = 630,
  ogVideo,
  ogArticlePublishedTime,
  ogArticleModifiedTime,
  ogArticleAuthor,
  ogArticleTags,
  twitterCard = 'summary_large_image',
  twitterSite = '@solinprimejc',
  twitterCreator,
  structuredData,
  additionalStructuredData,
  noIndex = false,
  siteName = 'SOL IN PRIME JC',
  locale = 'es_CL',
  alternateLanguages,
  pageType = 'home',
  lastModified,
  author,
}) => {
  // Usar la URL actual si canonicalUrl no se proporciona
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const finalCanonicalUrl = canonicalUrl || currentUrl;

  return (
    <Helmet>
      {/* Basic SEO */}
      <title>
        {title.length < 30
          ? `${title} | ${siteName} - Soluciones Industriales Empresariales`
          : title.length > 60
            ? title.substring(0, 57) + '...'
            : title}
      </title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={finalCanonicalUrl} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="revisit-after" content="7 days" />
      <meta name="language" content="Spanish" />
      <meta httpEquiv="content-language" content="es" />
      {author && <meta name="author" content={author} />}
      {lastModified && <meta name="last-modified" content={lastModified} />}

      {/* Open Graph / Facebook */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={finalCanonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:width" content={String(ogImageWidth)} />
      <meta property="og:image:height" content={String(ogImageHeight)} />
      {ogVideo && <meta property="og:video" content={ogVideo} />}

      {/* Article specific Open Graph tags */}
      {ogType === 'article' && ogArticlePublishedTime && (
        <meta property="article:published_time" content={ogArticlePublishedTime} />
      )}
      {ogType === 'article' && ogArticleModifiedTime && (
        <meta property="article:modified_time" content={ogArticleModifiedTime} />
      )}
      {ogType === 'article' && ogArticleAuthor && (
        <meta property="article:author" content={ogArticleAuthor} />
      )}
      {ogType === 'article' &&
        ogArticleTags &&
        ogArticleTags.map((tag, index) => (
          <meta key={`article-tag-${index}`} property="article:tag" content={tag} />
        ))}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {twitterCreator && <meta name="twitter:creator" content={twitterCreator} />}

      {/* Alternate languages */}
      {alternateLanguages &&
        alternateLanguages.map(altLang => (
          <link key={altLang.locale} rel="alternate" hrefLang={altLang.locale} href={altLang.url} />
        ))}

      {/* Structured Data - Main */}
      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}

      {/* Additional Structured Data */}
      {additionalStructuredData &&
        additionalStructuredData.map((data, index) => (
          <script key={`structured-data-${index}`} type="application/ld+json">
            {JSON.stringify(data)}
          </script>
        ))}

      {/* Page-specific metadata based on pageType */}
      {pageType === 'blog' && (
        <>
          <meta name="content-type" content="article" />
          <meta property="article:section" content="Blog" />
        </>
      )}
      {pageType === 'service' && (
        <>
          <meta name="content-type" content="service" />
          <meta property="business:service" content="true" />
        </>
      )}
    </Helmet>
  );
};

export default SEOHead;
